import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Plane, User, Briefcase, Star, ArrowRight, BookOpen, Languages, GraduationCap, TrendingUp, Filter, X, Search, MapPin, Clock, Award, DollarSign, Users } from 'lucide-react';
import heroImage from '../assets/Program/CPLHU.JPG';
import coursesDataJSON from '../data/courses.json';

// Icon mapping
const iconMap = {
  Star: Star,
  Plane: Plane,
  User: User,
  Briefcase: Briefcase,
  BookOpen: BookOpen,
  Languages: Languages,
  GraduationCap: GraduationCap,
};

// Get icon component from string
const getIconComponent = (iconName) => {
  const IconComponent = iconMap[iconName];
  return IconComponent ? <IconComponent /> : <Star />;
};

// --- Styled Components ---

const PageWrapper = styled.div`
  font-family: 'Inter', sans-serif;
  background: #ffffff;
  color: #1a202c;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;

  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      linear-gradient(135deg, rgba(0, 86, 179, 0.03) 0%, rgba(255, 215, 0, 0.02) 100%),
      radial-gradient(circle at 20% 80%, rgba(0, 61, 130, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 165, 0, 0.05) 0%, transparent 50%);
    pointer-events: none;
    z-index: 0;
  }
  
  > * {
    position: relative;
    z-index: 1;
  }
`;

const HeroSection = styled.section`
  text-align: center;
  padding: 160px 20px 120px;
  background: 
    linear-gradient(135deg, rgba(0, 31, 63, 0.95) 0%, rgba(0, 86, 179, 0.92) 50%, rgba(0, 61, 130, 0.94) 100%),
    url(${heroImage}) center/cover no-repeat fixed;
  color: white;
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: 
      radial-gradient(circle at 30% 50%, rgba(255, 215, 0, 0.15) 0%, transparent 40%),
      radial-gradient(circle at 70% 60%, rgba(255, 165, 0, 0.12) 0%, transparent 40%),
      radial-gradient(circle at 50% 30%, rgba(255, 255, 255, 0.08) 0%, transparent 30%);
    animation: gradientShift 15s ease-in-out infinite;
    pointer-events: none;
  }

  @keyframes gradientShift {
    0%, 100% {
      transform: translate(0, 0) rotate(0deg);
    }
    50% {
      transform: translate(5%, 5%) rotate(5deg);
    }
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 80px;
    background: linear-gradient(180deg, transparent 0%, #ffffff 100%);
    pointer-events: none;
  }

  h1 {
    font-size: clamp(3rem, 7vw, 5.2rem);
    font-weight: 900;
    line-height: 1.1;
    text-shadow: 0 4px 30px rgba(0,0,0,0.5);
    position: relative;
    z-index: 1;
    margin-bottom: 25px;
    animation: fadeInUp 0.8s ease-out;
    
    span {
      background: linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FFD700 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      display: inline-block;
      animation: shimmer 3s ease-in-out infinite;
      background-size: 200% 100%;
    }
  }

  @keyframes shimmer {
    0%, 100% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  p {
    font-size: clamp(1.2rem, 3vw, 1.45rem);
    max-width: 950px;
    margin: 0 auto;
    opacity: 0.96;
    position: relative;
    z-index: 1;
    line-height: 1.7;
    font-weight: 300;
    letter-spacing: 0.3px;
    animation: fadeInUp 0.8s ease-out 0.2s backwards;
    text-shadow: 0 2px 10px rgba(0,0,0,0.3);
  }

  @media (max-width: 768px) {
    padding: 120px 20px 80px;
  }
`;

const CoursesSection = styled.section`
  max-width: 95%;
  width: 100%;
  margin: 0 auto;
  padding: clamp(60px, 8vw, 100px) clamp(15px, 3vw, 40px) clamp(80px, 10vw, 120px);
  position: relative;

  @media (min-width: 1600px) {
    max-width: 1600px;
  }

  @media (min-width: 1920px) {
    max-width: 1800px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 10%;
    right: 5%;
    width: min(400px, 25vw);
    height: min(400px, 25vw);
    background: 
      radial-gradient(circle, rgba(0, 86, 179, 0.08) 0%, transparent 70%);
    border-radius: 50%;
    filter: blur(60px);
    pointer-events: none;
    animation: float 20s ease-in-out infinite;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 20%;
    left: 5%;
    width: min(350px, 22vw);
    height: min(350px, 22vw);
    background: 
      radial-gradient(circle, rgba(255, 165, 0, 0.06) 0%, transparent 70%);
    border-radius: 50%;
    filter: blur(60px);
    pointer-events: none;
    animation: float 18s ease-in-out infinite reverse;
  }

  @keyframes float {
    0%, 100% {
      transform: translate(0, 0) scale(1);
    }
    50% {
      transform: translate(30px, -30px) scale(1.1);
    }
  }
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 320px) 1fr;
  gap: clamp(25px, 3vw, 40px);
  align-items: start;
  position: relative;
  z-index: 1;

  @media (max-width: 1400px) {
    grid-template-columns: minmax(260px, 300px) 1fr;
  }

  @media (max-width: 1200px) {
    grid-template-columns: 280px 1fr;
    gap: clamp(20px, 2.5vw, 30px);
  }

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  @media (max-width: 640px) {
    gap: 20px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: clamp(50px, 7vw, 70px);
  position: relative;
  z-index: 1;
  padding: 0 clamp(15px, 3vw, 20px);

  h2 {
    font-size: clamp(2.2rem, 5vw, 3.2rem);
    font-weight: 900;
    background: linear-gradient(135deg, #001f3f 0%, #0056b3 50%, #001f3f 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 15px;
    position: relative;
    display: inline-block;
    animation: fadeInUp 0.6s ease-out;

    &::after {
      content: '';
      position: absolute;
      bottom: -15px;
      left: 50%;
      transform: translateX(-50%);
      width: 100px;
      height: 5px;
      background: linear-gradient(90deg, #FFD700, #FFA500, #FFD700);
      border-radius: 10px;
      box-shadow: 0 2px 15px rgba(255, 165, 0, 0.4);
    }

    &::before {
      content: '✈';
      position: absolute;
      top: -40px;
      left: 50%;
      transform: translateX(-50%);
      font-size: 2rem;
      opacity: 0.2;
      animation: planeFly 4s ease-in-out infinite;
    }
  }

  @keyframes planeFly {
    0%, 100% {
      transform: translateX(-50%) translateY(0);
    }
    50% {
      transform: translateX(-30%) translateY(-10px);
    }
  }

  p {
    font-size: clamp(1.05rem, 2.5vw, 1.2rem);
    color: #4a5568;
    max-width: 750px;
    margin: 30px auto 0;
    line-height: 1.8;
    animation: fadeInUp 0.6s ease-out 0.2s backwards;
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;


// --- Filter Components ---

const FiltersWrapper = styled.div`
  position: sticky;
  top: 20px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  overflow-x: hidden;
  align-self: flex-start;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #0056b3, #003d82);
    border-radius: 10px;
    
    &:hover {
      background: linear-gradient(135deg, #003d82, #002657);
    }
  }
  
  @media (max-width: 968px) {
    position: relative;
    top: 0;
    max-height: none;
    overflow-y: visible;
  }
`;

const FiltersBar = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fcff 100%);
  border-radius: clamp(16px, 2vw, 20px);
  padding: clamp(20px, 2.5vw, 25px);
  box-shadow: 0 8px 30px rgba(0, 31, 63, 0.12);
  border: 1px solid rgba(0, 86, 179, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(0, 86, 179, 0.04) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }
`;

const FiltersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  flex-wrap: wrap;
  gap: 10px;
  position: relative;
  z-index: 1;

  h3 {
    display: flex;
    align-items: center;
    gap: clamp(8px, 1vw, 10px);
    font-size: clamp(1.1rem, 1.3vw, 1.2rem);
    font-weight: 800;
    color: #001f3f;
    margin: 0;

    svg {
      color: #0056b3;
      width: clamp(18px, 2vw, 20px);
      height: clamp(18px, 2vw, 20px);
    }
  }
`;

const ClearFiltersButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${props => props.disabled 
    ? 'linear-gradient(135deg, #cbd5e0, #a0aec0)' 
    : 'linear-gradient(135deg, #ff6b6b, #ee5a6f)'};
  color: white;
  border: none;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  box-shadow: ${props => props.disabled 
    ? '0 2px 8px rgba(0, 0, 0, 0.1)' 
    : '0 4px 15px rgba(255, 107, 107, 0.3)'};
  opacity: ${props => props.disabled ? '0.6' : '1'};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 107, 107, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SearchBar = styled.div`
  position: relative;
  margin-bottom: clamp(20px, 2.5vw, 25px);
  z-index: 1;

  input {
    width: 100%;
    padding: clamp(10px, 1.2vw, 12px) clamp(35px, 4vw, 40px) clamp(10px, 1.2vw, 12px) clamp(14px, 1.5vw, 16px);
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    font-size: clamp(0.9rem, 1vw, 0.95rem);
    transition: all 0.3s ease;
    background: white;
    font-family: 'Inter', sans-serif;

    &:focus {
      outline: none;
      border-color: #0056b3;
      box-shadow: 0 0 0 3px rgba(0, 86, 179, 0.1);
    }

    &::placeholder {
      color: #a0aec0;
    }
  }

  svg {
    position: absolute;
    right: clamp(12px, 1.5vw, 14px);
    top: 50%;
    transform: translateY(-50%);
    color: #718096;
    pointer-events: none;
    width: 18px;
    height: 18px;
  }
`;

const FilterGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(16px, 2vw, 20px);
  position: relative;
  z-index: 1;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(8px, 1vw, 10px);

  label {
    font-weight: 700;
    color: #2d3748;
    font-size: clamp(0.85rem, 0.95vw, 0.9rem);
    margin-bottom: 5px;
    display: flex;
    align-items: center;
    gap: clamp(6px, 0.8vw, 8px);

    svg {
      width: 16px;
      height: 16px;
      color: #0056b3;
    }
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%230056b3' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 40px;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  color: #2d3748;

  &:focus {
    outline: none;
    border-color: #0056b3;
    box-shadow: 0 0 0 3px rgba(0, 86, 179, 0.1);
  }

  &:hover {
    border-color: #0056b3;
  }
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px 25px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fcff 100%);
  border-radius: 16px;
  box-shadow: 0 4px 15px rgba(0, 31, 63, 0.08);
  border: 1px solid rgba(0, 86, 179, 0.08);
  position: relative;
  z-index: 1;
  flex-wrap: wrap;
  gap: 15px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 15px 20px;
  }
`;

const ResultsCount = styled.div`
  font-size: 1.1rem;
  color: #4a5568;
  font-weight: 600;

  span {
    color: #0056b3;
    font-weight: 800;
    font-size: 1.3rem;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    
    span {
      font-size: 1.2rem;
    }
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #718096;
  position: relative;
  z-index: 1;

  svg {
    width: 80px;
    height: 80px;
    margin-bottom: 20px;
    opacity: 0.3;
    color: #cbd5e0;
  }

  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 10px;
    color: #2d3748;
  }

  p {
    font-size: 1.1rem;
    color: #718096;
  }
`;

const SortByWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SortLabel = styled.span`
  font-weight: 600;
  color: #4a5568;
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

const SortSelect = styled(Select)`
  min-width: 200px;

  @media (max-width: 768px) {
    flex: 1;
    min-width: 0;
  }
`;

const PriceRangeSlider = styled.div`
  padding: 10px 0;
`;

const SliderContainer = styled.div`
  position: relative;
  height: 6px;
  background: linear-gradient(to right, #e2e8f0, #cbd5e0);
  border-radius: 10px;
  margin: 20px 0;
`;

const SliderTrack = styled.div`
  position: absolute;
  height: 6px;
  background: linear-gradient(135deg, #0056b3, #003d82);
  border-radius: 10px;
  left: ${props => props.left}%;
  right: ${props => props.right}%;
  box-shadow: 0 2px 8px rgba(0, 86, 179, 0.3);
`;

const SliderInput = styled.input`
  position: absolute;
  width: 100%;
  height: 6px;
  background: none;
  pointer-events: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  outline: none;
  top: 0;
  left: 0;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    background: linear-gradient(135deg, #0056b3, #003d82);
    border: 3px solid white;
    border-radius: 50%;
    cursor: pointer;
    pointer-events: auto;
    box-shadow: 0 2px 8px rgba(0, 86, 179, 0.4);
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.2);
      box-shadow: 0 4px 12px rgba(0, 86, 179, 0.5);
    }
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    background: linear-gradient(135deg, #0056b3, #003d82);
    border: 3px solid white;
    border-radius: 50%;
    cursor: pointer;
    pointer-events: auto;
    box-shadow: 0 2px 8px rgba(0, 86, 179, 0.4);
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.2);
      box-shadow: 0 4px 12px rgba(0, 86, 179, 0.5);
    }
  }

  &:active::-webkit-slider-thumb {
    transform: scale(1.1);
  }

  &:active::-moz-range-thumb {
    transform: scale(1.1);
  }
`;

const PriceLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
  font-size: 0.85rem;
  color: #4a5568;
  font-weight: 600;

  span {
    padding: 4px 10px;
    background: linear-gradient(135deg, #f0f7ff, #e0f0ff);
    border-radius: 8px;
    border: 1px solid rgba(0, 86, 179, 0.15);
    color: #0056b3;
  }
`;

const CoursesContent = styled.div`
  min-width: 0;
  position: relative;
  z-index: 1;
`;

const CoursesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 320px), 1fr));
  gap: clamp(20px, 2.5vw, 30px);
  animation: fadeIn 0.8s ease-in-out;
  position: relative;
  z-index: 1;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (min-width: 1600px) {
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 35px;
  }

  @media (min-width: 1920px) {
    grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
    gap: 40px;
  }

  @media (max-width: 1400px) {
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
  }

  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
  }

  @media (max-width: 968px) {
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 340px), 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const CourseCard = styled(Link)`
  background: linear-gradient(135deg, #ffffff 0%, #f8fcff 100%);
  border-radius: 24px;
  text-decoration: none;
  color: inherit;
  box-shadow: 
    0 10px 40px rgba(0, 31, 63, 0.1),
    0 2px 8px rgba(0, 86, 179, 0.05);
  transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(0, 86, 179, 0.1);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(90deg, #0056b3, #FFD700, #FFA500, #0056b3);
    background-size: 200% 100%;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.5s ease;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 10% 20%, rgba(0, 86, 179, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 90% 80%, rgba(255, 165, 0, 0.03) 0%, transparent 50%);
    opacity: 0;
    transition: opacity 0.5s ease;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-15px) scale(1.03);
    box-shadow: 
      0 30px 60px rgba(0, 31, 63, 0.18),
      0 5px 20px rgba(255, 165, 0, 0.1),
      0 0 0 3px rgba(0, 86, 179, 0.1);
    border-color: rgba(255, 165, 0, 0.3);

    &::before {
      transform: scaleX(1);
      animation: shimmerGradient 2s linear infinite;
    }

    &::after {
      opacity: 1;
    }

    .icon-container {
      transform: scale(1.15) rotate(10deg);
      background: linear-gradient(135deg, #0056b3, #003d82);
      color: #ffffff;
      box-shadow: 
        0 8px 25px rgba(0, 86, 179, 0.4),
        0 0 30px rgba(255, 215, 0, 0.3);
    }

    .learn-more {
      gap: 14px;
      color: #FFA500;
      
      svg {
        transform: translateX(8px);
      }
    }

    h3 {
      background: linear-gradient(135deg, #001f3f, #0056b3);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  @keyframes shimmerGradient {
    0% {
      background-position: 0% 50%;
    }
    100% {
      background-position: 200% 50%;
    }
  }

  .card-header {
    padding: clamp(25px, 3vw, 35px) clamp(25px, 3vw, 35px) clamp(20px, 2.5vw, 25px);
    display: flex;
    align-items: flex-start;
    gap: clamp(15px, 2vw, 20px);
  }

  .icon-container {
    color: #0056b3;
    width: clamp(55px, 7vw, 70px);
    height: clamp(55px, 7vw, 70px);
    background: linear-gradient(135deg, #e6f2ff, #cce5ff, #b3d9ff);
    border-radius: clamp(16px, 2vw, 20px);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    box-shadow: 
      0 6px 20px rgba(0, 86, 179, 0.15),
      inset 0 2px 4px rgba(255, 255, 255, 0.5);
    position: relative;

    &::before {
      content: '';
      position: absolute;
      inset: -2px;
      border-radius: 22px;
      background: linear-gradient(135deg, #FFD700, #FFA500);
      opacity: 0;
      transition: opacity 0.5s ease;
      z-index: -1;
    }

    svg {
      width: clamp(26px, 3.5vw, 32px);
      height: clamp(26px, 3.5vw, 32px);
      filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
    }
  }

  .card-title-section {
    flex: 1;
    min-width: 0;
  }

  h3 {
    font-size: clamp(1.2rem, 1.5vw, 1.5rem);
    font-weight: 800;
    margin: 0 0 10px;
    color: #001f3f;
    line-height: 1.3;
    transition: all 0.4s ease;
  }

  .card-body {
    padding: 0 clamp(25px, 3vw, 35px) clamp(25px, 3vw, 35px);
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  .course-meta {
    display: flex;
    flex-wrap: wrap;
    gap: clamp(8px, 1.2vw, 12px);
    margin-bottom: clamp(15px, 2vw, 20px);
    padding-bottom: clamp(15px, 2vw, 20px);
    border-bottom: 1px solid rgba(0, 86, 179, 0.08);
  }

  .meta-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: clamp(0.8rem, 0.9vw, 0.85rem);
    color: #4a5568;
    font-weight: 600;
    padding: clamp(5px, 0.7vw, 6px) clamp(10px, 1.2vw, 12px);
    background: linear-gradient(135deg, #f8f9fa, #e9ecef);
    border-radius: 15px;
    border: 1px solid rgba(0, 86, 179, 0.08);

    svg {
      width: 14px;
      height: 14px;
      color: #0056b3;
    }
  }

  .rating-item {
    background: linear-gradient(135deg, #fff7e6, #ffe8b3);
    border-color: rgba(255, 165, 0, 0.2);

    svg {
      color: #FFA500;
      fill: #FFA500;
    }
  }

  p {
    font-size: 1rem;
    line-height: 1.8;
    color: #4a5568;
    flex-grow: 1;
    margin-bottom: 24px;
  }

  .tags {
    display: flex;
    gap: 10px;
    margin-bottom: 28px;
    flex-wrap: wrap;
  }

  .tag {
    background: linear-gradient(135deg, #f0f7ff, #e0f0ff);
    color: #1e3a8a;
    padding: 7px 16px;
    border-radius: 25px;
    font-size: 0.8rem;
    font-weight: 700;
    border: 1.5px solid rgba(0, 86, 179, 0.15);
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    box-shadow: 0 2px 8px rgba(0, 86, 179, 0.08);

    &:hover {
      background: linear-gradient(135deg, #0056b3, #003d82);
      color: #ffffff;
      border-color: transparent;
      transform: translateY(-2px) scale(1.05);
      box-shadow: 0 4px 15px rgba(0, 86, 179, 0.3);
    }
  }

  .card-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 24px;
    border-top: 2px solid rgba(0, 86, 179, 0.08);
  }

  .learn-more {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #0056b3;
    font-weight: 800;
    font-size: 1rem;
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);

    svg {
      transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    }
  }

  .course-level {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.88rem;
    color: #718096;
    font-weight: 600;
    padding: 6px 14px;
    background: linear-gradient(135deg, #f8f9fa, #e9ecef);
    border-radius: 20px;
    border: 1px solid rgba(0, 86, 179, 0.1);

    svg {
      width: 18px;
      height: 18px;
      color: #0056b3;
    }
  }

  @media (max-width: 768px) {
    .card-header {
      padding: 30px 30px 20px;
    }

    .card-body {
      padding: 0 30px 30px;
    }

    h3 {
      font-size: 1.35rem;
    }

    .icon-container {
      width: 60px;
      height: 60px;

      svg {
        width: 28px;
        height: 28px;
      }
    }
  }
`;

const CourseListingTemplate = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedDuration, setSelectedDuration] = useState('all');
  const [selectedCertification, setSelectedCertification] = useState('all');
  const [minRating, setMinRating] = useState('all');
  const [sortBy, setSortBy] = useState('default');

  // Price range slider state with actual numerical values
  const [priceMin, setPriceMin] = useState(5000);
  const [priceMax, setPriceMax] = useState(120000);

  // Load data from JSON
  const { hero, section, categories, courses, filters } = coursesDataJSON;

  // Extract unique levels from courses
  const levels = ['all', ...new Set(courses.map(c => c.level))];

  // Calculate slider percentages for visual display
  const minPrice = 5000;
  const maxPrice = 120000;
  const minPercent = ((priceMin - minPrice) / (maxPrice - minPrice)) * 100;
  const maxPercent = ((maxPrice - priceMax) / (maxPrice - minPrice)) * 100;

  // Apply all filters
  const filteredCourses = courses
    .filter(course => {
      // Category filter
      if (activeCategory !== 'all' && course.category !== activeCategory) return false;

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesTitle = course.title.toLowerCase().includes(query);
        const matchesDescription = course.description.toLowerCase().includes(query);
        const matchesTags = course.tags.some(tag => tag.toLowerCase().includes(query));
        const matchesLocation = course.location.toLowerCase().includes(query);
        if (!matchesTitle && !matchesDescription && !matchesTags && !matchesLocation) return false;
      }

      // Level filter
      if (selectedLevel !== 'all' && course.level !== selectedLevel) return false;

      // Location filter
      if (selectedLocation !== 'all' && course.location !== selectedLocation) return false;

      // Duration filter
      if (selectedDuration !== 'all' && course.duration !== selectedDuration) return false;

      // Certification filter
      if (selectedCertification !== 'all' && course.certification !== selectedCertification) return false;

      // Price range filter (numerical)
      if (course.price < priceMin || course.price > priceMax) return false;

      // Rating filter
      if (minRating !== 'all') {
        const minRatingValue = parseFloat(minRating);
        if (course.rating < minRatingValue) return false;
      }

      return true;
    })
    .sort((a, b) => {
      // Sorting
      if (sortBy === 'title-asc') return a.title.localeCompare(b.title);
      if (sortBy === 'title-desc') return b.title.localeCompare(a.title);
      if (sortBy === 'rating-high') return b.rating - a.rating;
      if (sortBy === 'rating-low') return a.rating - b.rating;
      if (sortBy === 'popular') return b.enrolledStudents - a.enrolledStudents;
      if (sortBy === 'level') return a.level.localeCompare(b.level);
      return 0; // default order
    });

  const clearFilters = () => {
    setActiveCategory('all');
    setSearchQuery('');
    setSelectedLevel('all');
    setSelectedLocation('all');
    setSelectedDuration('all');
    setSelectedCertification('all');
    setPriceMin(5000);
    setPriceMax(120000);
    setMinRating('all');
    setSortBy('default');
  };

  const hasActiveFilters =
    activeCategory !== 'all' ||
    searchQuery ||
    selectedLevel !== 'all' ||
    selectedLocation !== 'all' ||
    selectedDuration !== 'all' ||
    selectedCertification !== 'all' ||
    priceMin !== 5000 ||
    priceMax !== 120000 ||
    minRating !== 'all' ||
    sortBy !== 'default';

  // Format price for display
  const formatPrice = (price) => {
    if (price >= 1000) {
      return `$${(price / 1000).toFixed(0)}k`;
    }
    return `$${price}`;
  };

  return (
    <PageWrapper>
      <HeroSection>
        <h1>{hero.title} <span>{hero.titleHighlight}</span></h1>
        <p>{hero.description}</p>
      </HeroSection>

      <CoursesSection>
        <SectionHeader>
          <h2>{section.title}</h2>
          <p>{section.description}</p>
        </SectionHeader>

        <ContentWrapper>
          {/* Left Sidebar - Filters */}
          <FiltersWrapper>
            <FiltersBar>
              <FiltersHeader>
                <h3>
                  <Filter size={20} />
                  Filters
                </h3>
                <ClearFiltersButton
                  onClick={clearFilters}
                  disabled={!hasActiveFilters}
                >
                  <X size={16} />
                  Clear
                </ClearFiltersButton>
              </FiltersHeader>

              <SearchBar>
                <input
                  type="text"
                  placeholder="Search courses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <Search size={18} />
              </SearchBar>

              <FilterGrid>
                <FilterGroup>
                  <label>
                    <Briefcase size={16} />
                    Category
                  </label>
                  <Select
                    value={activeCategory}
                    onChange={(e) => setActiveCategory(e.target.value)}
                  >
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.label}
                      </option>
                    ))}
                  </Select>
                </FilterGroup>
                <FilterGroup>
                  <label>
                    <TrendingUp size={16} />
                    Course Level
                  </label>
                  <Select
                    value={selectedLevel}
                    onChange={(e) => setSelectedLevel(e.target.value)}
                  >
                    <option value="all">All Levels</option>
                    {levels.filter(l => l !== 'all').map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </Select>
                </FilterGroup>

                <FilterGroup>
                  <label>
                    <MapPin size={16} />
                    Location
                  </label>
                  <Select
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                  >
                    <option value="all">All Locations</option>
                    {filters.locations.map(location => (
                      <option key={location} value={location}>{location}</option>
                    ))}
                  </Select>
                </FilterGroup>

                <FilterGroup>
                  <label>
                    <Clock size={16} />
                    Duration
                  </label>
                  <Select
                    value={selectedDuration}
                    onChange={(e) => setSelectedDuration(e.target.value)}
                  >
                    <option value="all">Any Duration</option>
                    {filters.durations.map(duration => (
                      <option key={duration} value={duration}>{duration}</option>
                    ))}
                  </Select>
                </FilterGroup>

                <FilterGroup>
                  <label>
                    <Award size={16} />
                    Certification
                  </label>
                  <Select
                    value={selectedCertification}
                    onChange={(e) => setSelectedCertification(e.target.value)}
                  >
                    <option value="all">All Certifications</option>
                    {filters.certifications.map(cert => (
                      <option key={cert} value={cert}>{cert}</option>
                    ))}
                  </Select>
                </FilterGroup>

                <FilterGroup>
                  <label>
                    <DollarSign size={16} />
                    Price Range
                  </label>
                  <PriceRangeSlider>
                    <SliderContainer>
                      <SliderTrack left={minPercent} right={maxPercent} />
                      <SliderInput
                        type="range"
                        min="5000"
                        max="120000"
                        step="1000"
                        value={priceMin}
                        onChange={(e) => {
                          const value = Math.min(Number(e.target.value), priceMax - 1000);
                          setPriceMin(value);
                        }}
                        style={{ zIndex: priceMin > priceMax - 5000 ? 5 : 3 }}
                      />
                      <SliderInput
                        type="range"
                        min="5000"
                        max="120000"
                        step="1000"
                        value={priceMax}
                        onChange={(e) => {
                          const value = Math.max(Number(e.target.value), priceMin + 1000);
                          setPriceMax(value);
                        }}
                        style={{ zIndex: 4 }}
                      />
                    </SliderContainer>
                    <PriceLabels>
                      <span>{formatPrice(priceMin)}</span>
                      <span>{formatPrice(priceMax)}</span>
                    </PriceLabels>
                  </PriceRangeSlider>
                </FilterGroup>

                <FilterGroup>
                  <label>
                    <Star size={16} />
                    Minimum Rating
                  </label>
                  <Select
                    value={minRating}
                    onChange={(e) => setMinRating(e.target.value)}
                  >
                    <option value="all">Any Rating</option>
                    <option value="4.5">4.5+ Stars</option>
                    <option value="4.0">4.0+ Stars</option>
                    <option value="3.5">3.5+ Stars</option>
                  </Select>
                </FilterGroup>
              </FilterGrid>
            </FiltersBar>
          </FiltersWrapper>

          {/* Right Content - Courses */}
          <CoursesContent>
            <TopBar>
              <ResultsCount>
                Showing <span>{filteredCourses.length}</span> of <span>{courses.length}</span> courses
              </ResultsCount>
              <SortByWrapper>
                <SortLabel>Sort By:</SortLabel>
                <SortSelect
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="default">Default Order</option>
                  <option value="title-asc">Title (A-Z)</option>
                  <option value="title-desc">Title (Z-A)</option>
                  <option value="rating-high">Highest Rated</option>
                  <option value="rating-low">Lowest Rated</option>
                  <option value="popular">Most Popular</option>
                  <option value="level">Level</option>
                </SortSelect>
              </SortByWrapper>
            </TopBar>

            {filteredCourses.length > 0 ? (
              <CoursesGrid>
                {filteredCourses.map((course) => (
                  <CourseCard to={course.link} key={course.id}>
                <div className="card-header">
                  <div className="icon-container">{getIconComponent(course.icon)}</div>
                  <div className="card-title-section">
                    <h3>{course.title}</h3>
                  </div>
                </div>
                <div className="card-body">
                  <div className="course-meta">
                    <div className="meta-item">
                      <MapPin />
                      {course.location}
                    </div>
                    <div className="meta-item">
                      <Clock />
                      {course.duration}
                    </div>
                    <div className="meta-item">
                      <Award />
                      {course.certification}
                    </div>
                    <div className="meta-item rating-item">
                      <Star />
                      {course.rating}
                    </div>
                    <div className="meta-item">
                      <Users />
                      {course.enrolledStudents}+
                    </div>
                  </div>
                  <p>{course.description}</p>
                  <div className="tags">
                    {course.tags.map(tag => <span key={tag} className="tag">{tag}</span>)}
                  </div>
                  <div className="card-footer">
                    <div className="learn-more">
                      Learn More <ArrowRight size={18} />
                    </div>
                    <div className="course-level">
                      <TrendingUp />
                      {course.level}
                    </div>
                  </div>
                </div>
              </CourseCard>
            ))}
          </CoursesGrid>
        ) : (
          <NoResults>
            <Search />
            <h3>No courses found</h3>
            <p>Try adjusting your filters or search query</p>
          </NoResults>
        )}
          </CoursesContent>
        </ContentWrapper>
      </CoursesSection>
    </PageWrapper>
  );
};

export default CourseListingTemplate;